import {
	type CaptureFileJSONObject,
	type CollectionFieldAnchorManualFieldValue,
	type CollectionFieldAnchorP2PFieldValue,
	type CollectionModelFieldAnchorValue,
	type DistanceBearingPitch,
	IkeFieldType,
	type LocationAltitude,
	type LocationData,
	type LocationDataRaw,
} from '@ikegps/core-api';
import type { AnchorLocationData } from '@ikegps/core-api/src/common/anchor';
import type { AnchorPlacement } from '@ikegps/core-api/src/common/orientation';
import type { CollectionFieldAnchorLocationValue } from '@ikegps/core-api/src/public/collection.field';
import { IkeGeodesic } from '../../../geo/geodesic';
import { MultiPoint } from '../../../math';
import { IkeAngle } from '../../../unit';
import type { FormModelField } from '../../form/form.field';
import type { CollectionModelBase } from '../collection.base';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';
import { CollectionModelFieldLocation, extractLocationRaw } from './location.field';
import type { CaptureProvider } from './provider/capture.provider';
import { CollectionModelFieldVector } from './vector.field';

export type ModelFieldStateAnchor =
	| ModelFieldStateAnchorManual
	| ModelFieldStateAnchorP2P
	| ModelFieldStateAnchorLocation;

export interface ModelFieldStateAnchorManual {
	provider: 'manual';
	value: CollectionFieldAnchorManualFieldValue;
}

export interface ModelFieldStateAnchorLocation extends Partial<LocationDataRaw> {
	provider: 'manual' | 'gps' | 'edited';
	value: CollectionFieldAnchorLocationValue;
}

export interface ModelFieldStateAnchorP2P {
	provider: 'pointtopoint';
	/** List of captures used for P2P (Generally two) */
	captures: string[];
	value: {
		collectionTool: 'VECTOR';
		placement: AnchorPlacement;
		relatedSpanIds?: string[];
	};
}

export class ModelMutatorAnchor extends ModelMutator<FormModelField, CollectionModelFieldAnchor> {
	// Mutator to edit a vector anchor manually
	manualAnchor(newValue: CollectionFieldAnchorManualFieldValue | null) {
		if (newValue == null || newValue.distance == null) {
			this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, null);
			return;
		}

		const relatedSpanIds = (newValue.relatedSpanIds ?? [])
			.slice(0, CollectionModelFieldAnchor.placementSpanCount(newValue.placement))
			.filter((id): id is string => id != null);

		// Without related spans the bearing can never be derived, so a missing bearing means no value
		if (newValue.bearing == null && relatedSpanIds.length === 0) {
			this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, null);
			return;
		}

		const value: CollectionFieldAnchorManualFieldValue = {
			collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
			placement: newValue.placement,
			distance: newValue.distance,
			// Force the anchor bearing to be between 0 and two Pi (when one is stored)
			...(newValue.bearing != null && relatedSpanIds.length === 0
				? { bearing: IkeAngle.normalize(newValue.bearing) }
				: {}),
			// Sometimes pitch is not set, It is a required field
			pitch: newValue.pitch == null || isNaN(newValue.pitch) ? 0 : newValue.pitch,
			...(relatedSpanIds.length > 0 ? { relatedSpanIds } : {}),
		};

		this.state.setFieldValue<ModelFieldStateAnchorManual>(this.field.id, {
			provider: CollectionModelFieldAnchor.PROVIDER_MANUAL,
			value,
		});
	}

	// Mutator when adding a new p2p vector
	p2p(state: string[], placement: AnchorPlacement) {
		if (!Array.isArray(state)) {
			throw new Error(`Field ${this.field.id}: Invalid capture state.`);
		}
		if (state.length < 2) {
			throw new Error(`Field ${this.field.id}: Invalid capture count "${state.length}".`);
		}

		this.state.setFieldValue<ModelFieldStateAnchorP2P>(this.field.id, {
			provider: CollectionModelFieldAnchor.PROVIDER_P2P,
			captures: state.slice(),
			value: {
				collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
				placement,
			},
		});
	}

	// Method used by viewVectorManualEdit
	manual(newValue: CollectionFieldAnchorManualFieldValue) {
		// manualAnchor rebuilds the stored value in a canonical shape, so no pre-processing needed
		this.manualAnchor(newValue);
	}

	// Mutator when adding anchor with gps position
	gps(newValue: AnchorLocationData) {
		// Validate GPS coordinates
		if (!CollectionModelFieldLocation.isValidLocation(newValue)) {
			this.clear();
			return;
		}

		const value: AnchorLocationData = {
			latitude: newValue.latitude,
			longitude: newValue.longitude,
			altitude: newValue.altitude,
			collectionTool: newValue.collectionTool,
			placement: newValue.placement,
			relatedSpanIds: newValue.relatedSpanIds,
		};

		if (newValue.accuracy != null) {
			value.accuracy = newValue.accuracy;
		}

		const stateValue: ModelFieldStateAnchorLocation = {
			provider: 'gps',
			value,
		};

		this.state.setFieldValue(this.field.id, stateValue);
	}

	// Set anntenaHeight value
	setAntennaHeight(antennaHeight) {
		var stateValue = this.state.getFieldValue(this.field.id) as ModelFieldStateAnchorLocation;
		stateValue.antennaHeight = antennaHeight;
	}

	override copy(from: CollectionModelFieldAnchor): boolean {
		if (from.isPointToPoint()) {
			this.p2p(from.stateValue.captures, from.value.placement);
			return true;
		}

		if (from.isManual()) {
			this.manualAnchor(from.value);
			return true;
		}

		return false;
	}
}

export class CollectionModelFieldAnchor
	extends CollectionModelField<FormModelField, ModelFieldStateAnchor, CollectionModelFieldAnchorValue>
	implements CaptureProvider
{
	static readonly PROVIDER_MANUAL = 'manual' as const;
	static readonly PROVIDER_P2P = 'pointtopoint' as const;
	static readonly PROVIDER_GPS = 'gps';
	static readonly PROVIDER_EDITED = 'edited';

	static readonly COLLLECTION_TOOL_VECTOR = 'VECTOR';
	static readonly COLLLECTION_TOOL_LOCATION = 'LOCATION';

	static readonly DEFAULT_PLACEMENT: AnchorPlacement = 'Other';

	static TYPE: IkeFieldType = IkeFieldType.ANCHOR;

	static isAnchorField(f: CollectionModelField): f is CollectionModelFieldAnchor {
		return f.schema.type === CollectionModelFieldAnchor.TYPE;
	}

	static isCollectionWithVectorTool(
		value: CollectionModelFieldAnchorValue
	): value is CollectionFieldAnchorManualFieldValue {
		return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
	}

	static isCollectionWithLocationTool(value: CollectionModelFieldAnchorValue): value is AnchorLocationData {
		return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION;
	}

	private static readonly SPAN_COUNT_BY_PLACEMENT: Record<AnchorPlacement, number> = {
		Inline: 1,
		Bisect: 2,
		Other: 0,
	};

	/** How many related spans a placement references (0 for null/unknown placements). */
	static placementSpanCount(placement: AnchorPlacement | null | undefined): number {
		return placement ? (CollectionModelFieldAnchor.SPAN_COUNT_BY_PLACEMENT[placement] ?? 0) : 0;
	}

	/**
	 * The anchor bearing (radians, normalized to `[0, 2π)`) implied by its placement and the
	 * bearings of its related spans, or `null` when the placement does not derive a bearing
	 * (`Other`) or the required spans are missing / invalid.
	 *
	 * - **Inline** — 180° opposite the single related span's bearing.
	 * - **Bisect** — 180° opposite the midpoint bearing of two related spans, wrapping correctly
	 *   across the 360° boundary and placing the anchor on the convex side.
	 */
	static calculateBearing(
		placement: AnchorPlacement | null | undefined,
		spanBearings: ReadonlyArray<number>
	): number | null {
		if (placement === 'Inline') {
			const bearing = spanBearings[0];
			if (bearing == null || isNaN(bearing)) {
				return null;
			}

			return IkeAngle.flip(bearing);
		}

		if (placement === 'Bisect') {
			const spanBearingA = spanBearings[0];
			const spanBearingB = spanBearings[1];
			if (spanBearingA == null || spanBearingB == null || isNaN(spanBearingA) || isNaN(spanBearingB)) {
				return null;
			}

			const low = Math.min(spanBearingA, spanBearingB);
			const high = Math.max(spanBearingA, spanBearingB);
			const midpoint = (low + high) / 2;
			return IkeAngle.normalize(high - low < Math.PI ? midpoint + Math.PI : midpoint);
		}

		return null;
	}

	private _Mutator: ModelMutatorAnchor;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorAnchor(this);
		}
		return this._Mutator;
	}

	override get value(): CollectionModelFieldAnchorValue {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return null;
		}

		if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
			// If a placement is not provided, then we want to default to Other
			const locationState = stateValue.value;
			locationState.placement = locationState.placement || CollectionModelFieldAnchor.DEFAULT_PLACEMENT;
			return locationState;
		}

		if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR) {
			if (this.isManual()) {
				const stored = <CollectionFieldAnchorManualFieldValue>stateValue.value;

				// For Inline/Bisect the bearing is derived from the related spans, never the
				// stored scalar — this keeps it in sync as the surrounding spans move (UFO-531).
				const derived = this.calculateDerivedBearing();
				return derived != null && derived !== stored.bearing ? { ...stored, bearing: derived } : stored;
			}

			if (this.isPointToPoint()) {
				const vectorValue = MultiPoint.Points(this.getCaptures()) as CollectionFieldAnchorP2PFieldValue;
				// To be backwards compatible, vertical values should not have a sign on them
				vectorValue.vertical = Math.abs(vectorValue.vertical);
				vectorValue.collectionTool = CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
				vectorValue.placement = stateValue.value.placement;
				return vectorValue;
			}
		}

		throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue['provider']}"`);
	}

	/**
	 * Get the raw location data calculating alternative altitudes
	 *
	 * @remarks This cannot be null
	 */
	get raw(): Partial<LocationDataRaw> & Partial<LocationAltitude> {
		if (!this.isGPS()) {
			return {};
		}

		const stateValue = this.stateValue;
		const isAntennaHeightMissing = stateValue.antennaHeight == null || stateValue.antennaHeight === 0;
		if (isAntennaHeightMissing) {
			return {
				...extractLocationRaw(stateValue),
				deviceAltitude: stateValue.value.altitude,
				antennaHeight: undefined, // Destroy antennaHeight if it is 0
			};
		}

		return {
			...extractLocationRaw(stateValue),
			deviceAltitude: stateValue.antennaHeight + stateValue.value.altitude,
			groundAltitude: stateValue.value.altitude,
			antennaHeight: stateValue.antennaHeight,
		};
	}

	get provider(): 'manual' | 'pointtopoint' | 'gps' | 'edited' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldAnchor.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	get antennaHeight(): number {
		const stateValue = this.stateValue as ModelFieldStateAnchorLocation;
		if (stateValue == null || stateValue.antennaHeight == null) {
			return 0;
		}
		return stateValue.antennaHeight;
	}

	target(source: LocationData): LocationData {
		if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
			const bearing = this.value.bearing ?? 0;
			const distance = this.value.distance;
			return IkeGeodesic.projectLocation(source, bearing, distance);
		}

		if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
			return {
				altitude: this.value.altitude,
				longitude: this.value.longitude,
				latitude: this.value.latitude,
			};
		}
		return null;
	}

	distanceBearingPitch(source: LocationData): DistanceBearingPitch | null {
		if (!this.value) {
			return {
				distance: 0,
				bearing: 0,
				pitch: 0,
			};
		}

		if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
			return {
				distance: this.value.distance,
				bearing: this.value.bearing ?? 0,
				pitch: this.value.pitch,
			};
		}
		const stateValue = this.stateValue;
		if (!stateValue) {
			return null;
		}
		if (source == null) {
			return null;
		}

		if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
			return IkeGeodesic.getVector(source, this.stateValue.value as LocationData);
		}
		return null;
	}

	/** The related span field ids this anchor references, truncated to what its placement allows. */
	relatedSpanIds(): string[] {
		if (!this.isManual()) {
			return [];
		}
		const value = this.stateValue?.value;
		if (value == null) {
			return [];
		}
		return (value.relatedSpanIds ?? [])
			.slice(0, CollectionModelFieldAnchor.placementSpanCount(value.placement))
			.filter((id): id is string => id != null);
	}

	/** Bearings (radians) of the resolvable related spans, in order. */
	private relatedSpanBearings(): number[] {
		return CollectionModelFieldAnchor.spanBearings(this.collection, this.relatedSpanIds());
	}

	/**
	 * Resolve the given span field ids to their bearings (radians), skipping any that are not
	 * vector fields or have no valid bearing. Shared by the domain model and the anchor input UI
	 * so the span lookup lives in one place.
	 */
	static spanBearings(collection: CollectionModelBase, spanIds: ReadonlyArray<string>): number[] {
		const bearings: number[] = [];
		for (const id of spanIds) {
			const span = collection.getFieldDeep(id);
			if (span == null || !CollectionModelFieldVector.isVectorField(span)) {
				continue;
			}
			const bearing = span.value?.bearing;
			if (typeof bearing === 'number' && !isNaN(bearing)) {
				bearings.push(bearing);
			}
		}
		return bearings;
	}

	/**
	 * The anchor bearing (radians) implied by its placement and related spans, or `null`
	 * when the placement is `Other` or the related spans are missing / invalid.
	 */
	calculateDerivedBearing(): number | null {
		if (!this.isManual()) {
			return null;
		}
		const value = this.stateValue?.value;
		if (value == null) {
			return null;
		}
		return CollectionModelFieldAnchor.calculateBearing(value.placement, this.relatedSpanBearings());
	}

	/** True when this anchor's bearing is derived from its related spans rather than stored. */
	isBearingDerived(): boolean {
		return this.calculateDerivedBearing() != null;
	}

	isManual(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateAnchorManual,
		CollectionFieldAnchorManualFieldValue
	> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_MANUAL;
	}

	isPointToPoint(): this is CollectionModelField<
		FormModelField,
		ModelFieldStateAnchorP2P,
		CollectionFieldAnchorP2PFieldValue
	> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_P2P;
	}

	isGPS(): this is CollectionModelField<FormModelField, ModelFieldStateAnchorLocation, AnchorLocationData> {
		return this.provider === CollectionModelFieldAnchor.PROVIDER_GPS;
	}

	isTrueSize(): this is CollectionModelField<FormModelField, ModelFieldStateAnchorLocation> {
		return false;
	}

	// Get tool used for collection, by default we show a Vector tool
	get tool(): 'LOCATION' | 'VECTOR' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.value.collectionTool == null) {
			return CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
		}

		return stateValue.value.collectionTool;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	getCaptures(): CaptureFileJSONObject[] {
		const captures: CaptureFileJSONObject[] = [];
		const captureIds = this.getCaptureIds();
		for (const captureId of captureIds) {
			const capture = this.collection.state.getCapture(captureId);
			if (capture == null) {
				return [];
			}

			if (capture.orientation == null) {
				throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing orientation)`);
			}

			if (capture.calibrationid == null) {
				throw new Error(
					`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing calibrationId)`
				);
			}

			captures.push(capture);
		}
		return captures;
	}

	isCollectionLink(): boolean {
		return false;
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		if (!this.isPointToPoint()) {
			return [];
		}
		return this.stateValue.captures;
	}

	hasValue(): boolean {
		if (this.value != null) {
			if (this.tool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
				return CollectionModelFieldLocation.isValidLocation(<AnchorLocationData>this.value);
			}
			if (
				(<CollectionFieldAnchorManualFieldValue>this.value).distance === 0 &&
				(<CollectionFieldAnchorManualFieldValue>this.value).bearing === 0 &&
				(<CollectionFieldAnchorManualFieldValue>this.value).placement === null
			) {
				return false;
			}
			return true;
		}
		return false;
	}
}

registerFieldType(IkeFieldType.ANCHOR, (parent, schema) => new CollectionModelFieldAnchor(parent, schema));

/**
 * Resolve an anchor lead-length field to its distance/bearing/pitch vector regardless of the tool
 * it was collected with. A plain vector field returns its stored vector; an accurate (location)
 * anchor field is computed geodesically from `source` (the pole location), since accurate anchors
 * store a GPS position rather than a vector. Returns `null` when the field has no resolvable value.
 *
 * Centralises the logic the PLA exporters (DDS, Spida, Ocalc, PoleForeman, shapefile) need so a
 * fielded-but-unedited accurate anchor exports with the correct distance/direction (UFO-563).
 */
export function resolveAnchorLeadVector(
	field: CollectionModelField | null | undefined,
	source: LocationData | null
): DistanceBearingPitch | null {
	if (field == null || !field.hasValue()) {
		return null;
	}
	if (CollectionModelFieldVector.isVectorField(field)) {
		return field.value;
	}
	if (CollectionModelFieldAnchor.isAnchorField(field)) {
		return field.distanceBearingPitch(source);
	}
	return null;
}
